<template>
    <div class="news-marquee" v-if="items && items.length">
      <div class="swiper-container" ref="swiper">
        <!-- 画像表示域に重ねるナビ（縦中央） -->
        <div class="nav-overlay" ref="overlay">
          <div class="swiper-button-prev"></div>
          <div class="swiper-button-next"></div>
        </div>
  
        <div class="swiper-wrapper">
          <div class="swiper-slide" v-for="a in items" :key="a.id">
            <a :href="a.url" class="slide-link" rel="noopener">
              <div class="image-wrap">
                <img
                  :src="initialSrc(a)"
                  :srcset="srcSet(a)"
                  sizes="(max-width: 768px) 100vw, 960px"
                  :alt="a.image?.alt || a.title"
                  loading="lazy"
                  decoding="async"
                  @error="onImgError"
                  :data-fallback="fallbackSrc(a)"
                  :data-fallback2="PLACEHOLDER"
                  oncontextmenu="alert('(C) The Hochi Shimbun ');return false;"
                  onmousedown="return false;"
                  @dragstart.prevent
                  @load="syncOverlayHeight"
                />
              </div>
              <div class="meta">
                <h3 class="title">{{ a.title }}</h3>
                <time class="date" :datetime="pubDate(a)">{{ formatDate(pubDate(a)) }}</time>
              </div>
            </a>
          </div>
        </div>
  
        <div class="swiper-pagination"></div>
      </div>
    </div>
  </template>
  
  <script>
  import Swiper from 'swiper'
  import 'swiper/css/swiper.min.css'
  
  const PLACEHOLDER = '/images/swiper-noimg.png'
  
  export default {
    name: 'NewsMarquee',
    props: {
      items: { type: Array, default: () => [] },
      autoplay: { type: Number, default: 5000 }
    },
    data: () => ({
      swiper: null,
      resizeHandler: null
    }),
    computed: {
      PLACEHOLDER () { return PLACEHOLDER }
    },
    watch: {
      items: {
        immediate: true,
        handler (val) {
          if (val && val.length) {
            this.$nextTick(() => {
              if (!this.$refs.swiper) return
  
              if (!this.swiper) {
                const prevEl = this.$refs.overlay.querySelector('.swiper-button-prev')
                const nextEl = this.$refs.overlay.querySelector('.swiper-button-next')
  
                this.swiper = new Swiper(this.$refs.swiper, {
                  loop: true,
                  slidesPerView: 1,
                  autoplay: { delay: this.autoplay, disableOnInteraction: false },
                  pagination: { el: '.swiper-pagination', clickable: true },
                  navigation: { prevEl, nextEl },
                  keyboard: { enabled: true }
                })
  
                const el = this.$refs.swiper
                el.addEventListener('mouseenter', () => this.swiper.autoplay.stop())
                el.addEventListener('mouseleave', () => this.swiper.autoplay.start())
  
                this.syncOverlayHeight()
                this.resizeHandler = () => this.syncOverlayHeight()
                window.addEventListener('resize', this.resizeHandler)
              } else {
                this.swiper.update()
                this.syncOverlayHeight()
              }
            })
          }
        }
      }
    },
    beforeDestroy () {
      if (this.swiper) this.swiper.destroy(true, true)
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler)
    },
    methods: {
      // オーバーレイの高さを .image-wrap に同期
      syncOverlayHeight () {
        const wrap = this.$refs.swiper?.querySelector('.swiper-slide .image-wrap')
        if (wrap && this.$refs.overlay) {
          this.$refs.overlay.style.height = wrap.offsetHeight + 'px'
        }
      },
  
      // 入力データから画像URLを取得（string / {src} 両対応）
      imgSrc (a) { return a?.image?.src || a?.image || '' },
  
      // 初期 src：大画像が推測できればそれを、無ければプレースホルダー
      initialSrc (a) {
        const thumb = this.imgSrc(a)
        return thumb ? this.preferLarge(thumb) : PLACEHOLDER
      },
  
      // srcset：画像があるときだけ付与（無ければ attribute を外すため null）
      srcSet (a) {
        const thumb = this.imgSrc(a)
        if (!thumb) return null
        return `${this.preferLarge(thumb)} 960w, ${thumb} 480w`
      },
  
      // 第1フォールバック：サムネ or プレースホルダー
      fallbackSrc (a) {
        const thumb = this.imgSrc(a)
        return thumb || PLACEHOLDER
      },
  
      // 公開日
      pubDate (a) { return a?.publishedAt || a?.published_at || a?.date || '' },
  
      // サムネ → 大画像（例: 20250819-OHT1I51292-N.jpg → 20250819-OHT1I51292-L.jpg）
      preferLarge (src) {
        if (!src) return ''
        try {
          const [path, query = ''] = src.split('?')
          const segs = path.split('/')
          let file = segs.pop()
          const m = file.match(/^([a-z]?)(\d{8}-[^.]+)-([A-Z])(\.[a-z0-9]+)$/i)
          if (m) {
            const base = m[2]
            const ext  = m[4]
            file = base + '-L' + ext
          } else {
            file = file.replace(/-[A-Z](?=\.[a-z0-9]+$)/i, '-L')
          }
          segs.push(file)
          return segs.join('/') + (query ? '?' + query : '')
        } catch (e) {
          return src
        }
      },
  
      // 段階フォールバック：大 → サムネ/PH → PH
      onImgError (e) {
        const fb1 = e.target.dataset.fallback
        const fb2 = e.target.dataset.fallback2
        if (fb1 && e.target.src !== fb1) {
          e.target.src = fb1
          e.target.dataset.fallback = fb2 || ''
          return
        }
        if (fb2 && e.target.src !== fb2) {
          e.target.src = fb2
        }
      },
  
      formatDate (v) {
        if (!v) return ''
        const d = new Date(v)
        const y = d.getFullYear()
        const m = String(d.getMonth()+1).padStart(2,'0')
        const day = String(d.getDate()).padStart(2,'0')
        const hh = String(d.getHours()).padStart(2,'0')
        const mm = String(d.getMinutes()).padStart(2,'0')
        return `${y}年${m}月${day}日 ${hh}:${mm}`
      }
    }
  }
  </script>
  
  <style scoped>
  .news-marquee .swiper-container { position: relative; width: 100%; margin-top:16px;margin-bottom:40px;}
  
  /* 画像は“全体表示”でレターボックス（16:9枠） */
  .image-wrap {
    position: relative;
    padding-top: 56.25%; /* 16:9 */
    overflow: hidden;
    background: #e5f1f5;    /* 帯色（必要に応じて変更） */
  }
  .image-wrap img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;     /* 枠内に収める */
    object-position: center;
  }
  
  /* 画像表示域の縦中央にナビ配置 */
  .nav-overlay {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 0;                /* JS で .image-wrap 高に同期 */
    z-index: 2;
    pointer-events: none;
  }
  .nav-overlay .swiper-button-prev,
  .nav-overlay .swiper-button-next {
    top: 50%;
    transform: translateY(-50%);
    pointer-events: auto;
    color: #fff;
    text-shadow: 0 1px 2px rgba(0,0,0,.4);
  }
  .nav-overlay .swiper-button-prev { left: 8px; }
  .nav-overlay .swiper-button-next { right: 8px; }
  
  .slide-link { display: block; color: inherit; text-decoration: none; }
  .meta { padding: 12px 8px 16px; }
  .title {
    margin: 6px 0 4px;
    font-weight: 700;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .date { color: #9aa0a6; font-size: .9rem; }
  .swiper-pagination{
    position: relative;
    margin-top:-5px;
  }
  .swiper-pagination-bullet { opacity: 1; background: #cfd8dc; }
  .swiper-pagination-bullet-active { background: #607d8b; }

  /* ====== Hover / Focus 演出（ナビ・ドット・タイトル） ====== */

/* ナビボタン：丸背景＋色変化＋軽い拡大 */
.nav-overlay .swiper-button-prev,
.nav-overlay .swiper-button-next {
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  background: rgba(0, 0, 0, .28);
  transition: background-color .2s ease, transform .2s ease, color .2s ease;
}
.nav-overlay .swiper-button-prev::after,
.nav-overlay .swiper-button-next::after {
  font-size: 18px; /* 矢印アイコンを少し大きく */
}

/* マウスオーバー時に濃く・少し拡大、キーボードフォーカスでも同様 */
.news-marquee:hover .nav-overlay .swiper-button-prev,
.news-marquee:hover .nav-overlay .swiper-button-next,
.nav-overlay .swiper-button-prev:focus-visible,
.nav-overlay .swiper-button-next:focus-visible {
  background: rgba(0, 0, 0, .55);
}
.nav-overlay .swiper-button-prev:hover,
.nav-overlay .swiper-button-next:hover {
  transform: translateY(-50%) scale(1.06);
}

/* 無効化時の視認性 */
.swiper-button-disabled {
  opacity: .35 !important;
  cursor: default;
}

/* ページネーションのドット */
.swiper-pagination-bullet {
  opacity: 1;
  background: #cfd8dc;
  transition: background-color .2s ease, transform .2s ease;
}
.swiper-pagination-bullet:hover {
  transform: scale(1.16);
  background: #8aa7b5;
}
.swiper-pagination-bullet-active {
  background: #0b2f4a; /* サイトのキーカラーに合わせてOK */
}

/* タイトルのリンク色をホバーで強調 */
.slide-link:hover .title,
.slide-link:focus .title {
  color: #0b2f4a;
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* 画像は“全体表示”を保ったまま、ほんの少しだけ強調 */
.slide-link:hover .image-wrap img {
  filter: saturate(1.02) brightness(1.02);
  transition: filter .2s ease;
}

/* 動きが苦手なユーザー設定に配慮 */
@media (prefers-reduced-motion: reduce) {
  .nav-overlay .swiper-button-prev,
  .nav-overlay .swiper-button-next,
  .swiper-pagination-bullet,
  .slide-link .image-wrap img {
    transition: none !important;
  }
}

/* アニメーションの滑らかさ */
.image-wrap img { transition: filter .2s ease; }

/* ホバー/キーボードフォーカスで少し明るく（+ ほんのりコントラスト/彩度） */
.slide-link:hover .image-wrap img,
.slide-link:focus-visible .image-wrap img {
  filter: brightness(1.06) contrast(1.02) saturate(1.03);
}

/* 動きが苦手なユーザー設定に配慮 */
@media (prefers-reduced-motion: reduce) {
  .image-wrap img { transition: none; }
}
  </style>